import { AwaitingItem, Delivery, DestinationGroup, NetworkLocation } from '../types';

/**
 * Izbor tokova: šta pravac nosi i pod kojim ključem.
 *
 * Izdvojeno iz App-a da bi se moglo TESTIRATI: ova pravila su se lomila na
 * stvarnim pravcima (North Star bez robe iz magacina, Ring i susedni grad),
 * a živela su zarobljena u komponenti gde ih nijedan test ne vidi.
 */

export const MPO = 'mpo:';
export const KUPAC = 'kupac:';

/**
 * Centralni magacin. Glavni tovar svake ture dolazi odavde i to se ne menja
 * ni kad tura kreće sa drugog mesta — po robu se ide baš tamo, jer je tamo
 * ima.
 *
 * Stoji kao imenovana vrednost umesto rasutog niza po uslovima, otkad je
 * polaziste ture postalo promenljivo: sa golim „MAG 021" u pet uslova se ne
 * vidi koje od njih znaci „centralni", a koje „odakle se krece".
 */
export const CENTRALNI = 'MAG 021';

/**
 * Preuzimanje ima svoj prefiks iako je i ono lokacija iz mreže. Bez toga bi
 * „podigni aparat u Apatinu" i „ostavi prenos za Apatin" delili isti ključ, pa
 * bi jedno gasilo drugo — a to su dve različite stanice na istoj turi.
 */
export const PREUZIMANJE = 'preuzimanje:';

/**
 * Nalog stariji od ovoga se sklanja sa spiska — ostavlja se po strani.
 *
 * Odluka vlasnika procesa 08.09.2026: „stare naloge sklanjaj, ako ikad
 * zatrebaju oživeću neki koji zatreba." Ne brišu se: stoje sklopljeni i mogu na
 * turu kad zatrebaju, ali ne ulaze u predloge i ne zatrpavaju živi posao.
 *
 * Zašto baš mesec dana: prevoz ide svakodnevno, a MPO čekanje se prijavljuje već
 * posle dva dana. Nalog koji je mesec dana neizmiren nije roba koja čeka nego
 * papir koji je zaostao. Izmereno istog dana: preko 30 dana ima 21 stavku na 17
 * naloga, a ispod ostaje 164 — rez pada u prazno, ne kroz živi posao.
 *
 * Ranije je ovde stajalo da se prijem iz 002 knjiži isti dan „jer ulaz i izlaz
 * nose isti datum". To ne stoji: DATUM je datum DOKUMENTA, koji ERP prepisuje sa
 * naloga. Stvarno vreme upisa je u RECNO, i prijem po pravilu dolazi POSLE
 * isporuke. Zaključak ostaje isti, dokaz je bio pogrešan.
 */
export const ZAOSTALO_DANA = 30;

/**
 * Grupa je TOK: od tačke A do tačke B. Zaostali nalozi (verovatno
 * neproknjižen prijem) idu u zaseban tok istog pravca, da ne zatrpavaju
 * živi posao i ne teraju „čeka 230 d" na vrh spiska.
 */
export function buildFlowGroups(items: AwaitingItem[]): DestinationGroup[] {
  const byFlow = new Map<string, AwaitingItem[]>();

  for (const item of items) {
    const destination = item.destination ?? 'Bez odredišta — traži potvrdu';
    const origin = item.origin ?? '?';
    const stale = item.waitingDays > ZAOSTALO_DANA;
    // Eksterni razvoz ide u svoj tok i kad je star: on se ne planira, pa mu
    // „zaostalo" ne znači ništa — samo bi ga sakrilo u drugu fioku.
    const external = item.isExternalTransport === true;
    // Roba za kupca ide u SVOJ tok: nije interni nalog, ne zastareva posle mesec
    // dana (kupac i dalje čeka) i nikad ne ide preko magacina.
    const kupac = item.zaKupca != null;
    const key = `${kupac ? 'kupac|' : external ? 'eksterno|' : stale ? 'zaostalo|' : ''}${origin}→${destination}`;

    const bucket = byFlow.get(key);
    if (bucket) bucket.push(item);
    else byFlow.set(key, [item]);
  }

  return Array.from(byFlow.entries())
    .map(([key, groupItems]) => {
      const overdue = groupItems.filter((i) => i.isOverdue);
      const isStale = key.startsWith('zaostalo|');
      const isExternal = key.startsWith('eksterno|');
      const zaKupce = key.startsWith('kupac|');
      const [origin, destination] = key
        .replace('zaostalo|', '')
        .replace('eksterno|', '')
        .replace('kupac|', '')
        .split('→');

      return {
        flowKey: key,
        origin: origin === '?' ? null : origin,
        destination,
        items: groupItems,
        totalQuantity: groupItems.reduce((sum, i) => sum + i.quantity, 0),
        oldestWaitingDays: Math.max(...groupItems.map((i) => i.waitingDays)),
        overdueCount: overdue.length,
        overdueQuantity: overdue.reduce((sum, i) => sum + i.quantity, 0),
        hasOverdue: overdue.length > 0,
        needsReviewCount: groupItems.filter((i) => i.needsReview).length,
        isStale,
        isExternal,
        ...(zaKupce ? { zaKupce: true } : {})
      };
    })
    // Prednost ima tok gde najviše robe prelazi prag čekanja, ne onaj koji
    // ima jedan zaboravljen komad od pre tri meseca.
    .sort((a, b) => {
      if (a.overdueQuantity !== b.overdueQuantity) return b.overdueQuantity - a.overdueQuantity;
      return b.totalQuantity - a.totalQuantity;
    });
}

/**
 * Oznaka „MAG 021" / „MPO 004" u par (vrsta, šifra), kakav ide u nacrt ture.
 * Vraća null kad oznaka nije tog oblika — šifra se ne pogađa.
 */
export function refOf(reference: string): { kind: number; code: string } | null {
  const match = reference.match(/^(MPO|MAG) (\d{3})$/);
  if (!match) return null;

  // LocationKind: 0 = Store, 1 = Warehouse.
  return { kind: match[1] === 'MAG' ? 1 : 0, code: match[2] };
}

/** Grad u kom lokacija stoji, po oznaci tipa MPO 009 / MAG 029. */
export function cityOf(reference: string, locations: NetworkLocation[]): string | null {
  const match = reference.match(/^(MPO|MAG) (\d{3})$/);
  if (!match) return null;

  const kind = match[1] === 'MAG' ? 'Warehouse' : 'Store';
  return locations
    .find((l) => l.kind === kind && l.code === match[2])
    ?.city?.trim().toUpperCase() ?? null;
}

/**
 * Prodavnice u istom mestu odakle tura krece.
 *
 * Vozilo je tu vec ujutru, pa njihova roba ne trazi svracanje. Racuna se od
 * POLAZISTA, ne od centralnog magacina: tura iz Kule ima kulske prodavnice
 * „pri ruci", a somborske ne.
 */
export function startCityStores(
  locations: NetworkLocation[], base: string = CENTRALNI
): Set<string> {
  const startCity = cityOf(base, locations);

  return new Set(
    locations
      .filter((l) => l.kind === 'Store' && l.city?.trim().toUpperCase() === startCity)
      .map((l) => `MPO ${l.code}`));
}

/** Gradovi u kojima mesta pravca stoje. */
export function routeCities(destinations: string[], locations: NetworkLocation[]): Set<string> {
  const cities = new Set<string>();
  for (const destination of destinations) {
    const city = cityOf(destination, locations);
    if (city) cities.add(city);
  }
  return cities;
}

/**
 * Tokovi koje pravac nosi ka JEDNOM odredištu:
 *
 * - iz magacina — glavni tovar;
 * - sa objekata u polaznom mestu (vozilo ionako kreće odatle);
 * - sa drugih mesta pravca — direktna razmena, uzima se usput;
 * - sa objekata u GRADU nekog mesta pravca: lokalni magacin Subotice (029)
 *   stoji u istom gradu kao MPO 009 — tura je tamo svakako.
 */
export function flowKeysTo(
  destination: string,
  groups: DestinationGroup[],
  locations: NetworkLocation[],
  onRoute: Set<string>,
  routeCitiesSet: Set<string>,
  startCityStoreSet: Set<string>,
  base: string = CENTRALNI
): string[] {
  return groups
    .filter((g) => !g.isStale && !g.isExternal && g.destination === destination)
    .filter((g) => {
      // Centralni magacin je glavni tovar; polaziste je roba koja je vec tu.
      // Kad se krece iz centralnog, to je isto mesto i uslov se ne menja.
      if (g.origin === null || g.origin === CENTRALNI || g.origin === base) return true;
      if (startCityStoreSet.has(g.origin) || onRoute.has(g.origin)) return true;

      const city = cityOf(g.origin, locations);
      return city !== null && routeCitiesSet.has(city);
    })
    .map((g) => MPO + g.flowKey);
}

/**
 * Pravac bira TOKOVE — sve što na toj turi treba pokupiti i gde treba, redom
 * mesta iz šablona. Na kraj idu tokovi SA mesta pravca (ili iz njihovih
 * gradova) ka odredištima van njega — oni ne prave stanicu, podrazumevano idu
 * „u magacin".
 */
export function flowsForDestinations(
  destinations: string[],
  groups: DestinationGroup[],
  locations: NetworkLocation[],
  base: string = CENTRALNI
): string[] {
  const onRoute = new Set(destinations);
  const routeCitiesSet = routeCities(destinations, locations);
  const startCity = startCityStores(locations, base);
  const chosen: string[] = [];
  const seen = new Set<string>();

  const take = (keys: string[]) => {
    for (const key of keys) {
      if (!seen.has(key)) {
        seen.add(key);
        chosen.push(key);
      }
    }
  };

  for (const destination of destinations) {
    take(flowKeysTo(destination, groups, locations, onRoute, routeCitiesSet, startCity, base));
  }

  // Sve što stoji na mestima pravca — ili u njihovim gradovima — za bilo
  // koje odredište. CENTRALNI MAGACIN SE NE RAČUNA: njegova roba je glavni
  // tovar i ide samo ka odredištima pravca. Bez ovoga je West, koji ima
  // somborsku prodavnicu kao stanicu, povukao SVE iz magacina — i tura je
  // morala u pola Vojvodine da isporuči.
  for (const g of groups) {
    // Roba za kupca ne ulazi ovim putem: ona ide PRAVO u svoj objekat, pa bi tok
    // sa mesta pravca ka objektu van pravca turi dodao skretanje. Ulazi samo kad
    // je njen objekat na pravcu (flowKeysTo) — ili kad je operater doda sam.
    if (g.isStale || g.isExternal || g.zaKupce || g.origin === null || g.origin === CENTRALNI) continue;

    const city = cityOf(g.origin, locations);
    if (onRoute.has(g.origin) || (city !== null && routeCitiesSet.has(city))) {
      take([MPO + g.flowKey]);
    }
  }

  return chosen;
}

/** Stanica predloga, koliko izbor treba da zna o njoj. */
export interface ProposalStopLike {
  kind: string;
  isPickup?: boolean;
  locationKind: string | null;
  locationCode: string | null;
  documentKey: string | null;
}

/**
 * Izbor za učitan pravac, REDOM PREDLOGA: predlog je već složio kupce i
 * objekte najbolje što ume, a tok ulazi na mesto svog odredišta u tom redu.
 * Ranije su tokovi išli ispred svih kupaca, pa je tura ulazila u grad do
 * MPO-a tačkom 1 i vraćala se kupcima uz ukrštanja.
 */
export function templateSelection(
  proposalStops: ProposalStopLike[],
  route: string[],
  groups: DestinationGroup[],
  locations: NetworkLocation[],
  base: string = CENTRALNI
): string[] {
  const onRoute = new Set(route);
  const routeCitiesSet = routeCities(route, locations);
  const startCity = startCityStores(locations, base);
  const chosen: string[] = [];
  const seen = new Set<string>();

  const take = (keys: string[]) => {
    for (const key of keys) {
      if (!seen.has(key)) {
        seen.add(key);
        chosen.push(key);
      }
    }
  };

  for (const stop of proposalStops) {
    if (stop.kind === 'CustomerAddress') {
      if (stop.documentKey) take([KUPAC + stop.documentKey]);
      continue;
    }

    if (!stop.locationCode) continue;

    const destination = `${stop.locationKind === 'Warehouse' ? 'MAG' : 'MPO'} ${stop.locationCode}`;

    if (stop.isPickup) {
      take([PREUZIMANJE + destination]);
      continue;
    }

    take(flowKeysTo(destination, groups, locations, onRoute, routeCitiesSet, startCity, base));
  }

  // Mesta pravca bez stanice u predlogu, pa tokovi ka odredištima van njega.
  take(flowsForDestinations(route, groups, locations, base));

  return chosen;
}

/**
 * Roba za kupce koja ide U OBJEKAT, kao stavke tokova.
 *
 * Kupac dolazi sam (ili ga vozi prodavnica), a roba stoji drugde — klima iz
 * magacina 021 za kupca koji dolazi u Kulu (račun 004-1-5500, 19.10.2026). Za
 * operatera je to tok kao i svaki drugi: od mesta gde roba stoji do objekta, pa
 * staje u istu stanicu sa ostalom robom za taj objekat, a preuzimanje se dodaje
 * samo kad roba nije u polaznom magacinu. Ulazi samo ono što server kaže da sme
 * na turu (zna se odakle se diže, objekat ima tačku).
 */
export function stavkeZaObjekte(deliveries: Delivery[]): AwaitingItem[] {
  return deliveries
    .filter((d) => d.uObjekat && d.uObjekat.izvor && d.canBePlanned)
    .flatMap((d) => d.items
      .filter((i) => !i.isService)
      .map((i): AwaitingItem => ({
        articleCode: i.articleCode,
        articleName: i.articleName,
        quantity: i.quantity,
        waitingSince: d.issuedOn,
        waitingDays: d.waitingDays,
        isOverdue: d.isOverdue,
        destination: `MPO ${d.uObjekat!.kod}`,
        needsReview: false,
        rawDestinationText: null,
        sourceDocument: d.documentKey,
        origin: d.uObjekat!.izvor,
        zaKupca: { documentKey: d.documentKey, customer: d.customer, vrsta: d.uObjekat!.vrsta }
      })));
}
